import { useEffect, useRef, type RefObject } from "react";

interface TouchSession {
  startX: number;
  scrollLeft: number;
  scrollEl: HTMLElement | null;
}

/** 标签栏内触摸时隔离外层主滚动，并手动驱动横向滚动 */
export function useTabsRowTouchIsolation(
  tabsRowRef: RefObject<HTMLElement | null>,
  tabsScrollRef: RefObject<HTMLElement | null>
): void {
  const sessionRef = useRef<TouchSession | null>(null);

  useEffect(() => {
    const row = tabsRowRef.current;
    if (!row) {
      return undefined;
    }

    const onTouchStart = (e: TouchEvent) => {
      const scrollEl = tabsScrollRef.current;
      sessionRef.current = {
        startX: e.touches[0]?.clientX ?? 0,
        scrollLeft: scrollEl?.scrollLeft ?? 0,
        scrollEl,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const session = sessionRef.current;
      if (!session) {
        return;
      }
      if (e.cancelable) {
        e.preventDefault();
      }
      const scrollEl = session.scrollEl;
      if (!scrollEl || scrollEl.scrollWidth <= scrollEl.clientWidth + 1) {
        return;
      }
      const x = e.touches[0]?.clientX ?? session.startX;
      scrollEl.scrollLeft = session.scrollLeft + (session.startX - x);
      scrollEl.dispatchEvent(new Event("scroll"));
    };

    const clear = () => {
      sessionRef.current = null;
    };

    row.addEventListener("touchstart", onTouchStart, { passive: true, capture: true });
    row.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    row.addEventListener("touchend", clear, { capture: true });
    row.addEventListener("touchcancel", clear, { capture: true });

    return () => {
      row.removeEventListener("touchstart", onTouchStart, { capture: true });
      row.removeEventListener("touchmove", onTouchMove, { capture: true });
      row.removeEventListener("touchend", clear, { capture: true });
      row.removeEventListener("touchcancel", clear, { capture: true });
    };
  }, [tabsRowRef, tabsScrollRef]);
}
